import React, { useState, useEffect, useRef } from "react";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ScraperJob } from "./types";
import useSite from "@/hooks/useSite";

const SCRAPER_API_URL = import.meta.env.VITE_SCRAPER_API_URL || "http://localhost:8000/api/v1";
const SCRAPER_API_KEY = import.meta.env.VITE_SCRAPER_API_KEY || "";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  tokensUsed?: number;
}

export default function AINewChat() {
  const [jobs, setJobs] = useState<ScraperJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toastAlert = useToastAlert();
  const { site } = useSite();

  const getHeaders = () => ({
    "Content-Type": "application/json",
    "X-Service-Key": SCRAPER_API_KEY,
    "X-Site-Id": site?.siteId?.toString() || "0"
  });

  // Fetch available jobs
  useEffect(() => {
    // Only fetch if site is loaded
    if (!site?.siteId || site.siteId <= 0) {
      setLoadingJobs(false);
      return;
    }

    const fetchJobs = async () => {
      try {
        const response = await fetch(`${SCRAPER_API_URL}/chat/jobs`, {
          headers: getHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setJobs(data.jobs || []);
          if (data.jobs?.length > 0) {
            setSelectedJobId(data.jobs[0].job_id);
          }
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, [site?.siteId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || !selectedJobId) return;

    const userMessage: ChatMessage = {
      role: "user",
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch(`${SCRAPER_API_URL}/chat`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          job_id: selectedJobId,
          question: userMessage.content,
          model: "gpt-4o-mini"
        })
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: data.answer,
          timestamp: new Date(),
          tokensUsed: data.tokens_used?.total
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to get response");
        // Add error message to chat
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `Error: ${data.error || "Failed to get response"}`,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toastAlert.displayToast(Severity.Error, "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  const selectedJob = jobs.find(j => j.job_id === selectedJobId);

  if (loadingJobs) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Ingestion Test — Raw Search</h2>
        <p className="text-sm text-slate-500">
          Verify that your content was ingested and indexed correctly. No persona, workflows, or routing rules are applied here — pure vector search only.
        </p>
      </div>

      {/* Purpose callout */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
        <p className="font-semibold mb-1">What this is for</p>
        <p>Use this to confirm your scraped or uploaded content is searchable before configuring your bot. Ask questions you expect your bot to answer — if the right content surfaces here, your ingestion pipeline is working.</p>
        <p className="mt-2 text-blue-700">
          Ready to test with your full bot persona and settings?{" "}
          <a href="/ai-new/settings" className="underline font-medium hover:text-blue-900">
            Go to Chatbot Configuration → Test Chat tab
          </a>
        </p>
      </div>

      {/* Job Selector */}
      <div className="mb-4 p-4 bg-slate-50 rounded-lg">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Knowledge Base (Scraper Job)
        </label>
        {jobs.length === 0 ? (
          <div className="text-slate-500 text-sm">
            No completed scraper jobs available. Create and complete a scraper job first.
          </div>
        ) : (
          <div className="flex gap-4 items-center">
            <select
              value={selectedJobId}
              onChange={(e) => {
                setSelectedJobId(e.target.value);
                setMessages([]);
              }}
              className="flex-1 p-2 border rounded-md text-sm"
            >
              {jobs.map((job) => (
                <option key={job.job_id} value={job.job_id}>
                  {job.name} ({job.pages_scraped} pages)
                </option>
              ))}
            </select>
            {selectedJob && (
              <span className="text-xs text-slate-500">
                {selectedJob.pages_scraped} pages scraped
              </span>
            )}
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-white border rounded-lg overflow-hidden min-h-[400px]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              <p className="text-lg mb-2">Start a conversation</p>
              <p className="text-sm">
                Ask questions about the content from "{selectedJob?.name || 'selected job'}"
              </p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-100 text-slate-800"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                  <div
                    className={`text-xs mt-1 ${
                      msg.role === "user" ? "text-blue-200" : "text-slate-400"
                    }`}
                  >
                    {msg.timestamp.toLocaleTimeString()}
                    {msg.tokensUsed && ` • ${msg.tokensUsed} tokens`}
                  </div>
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 p-3 rounded-lg flex items-center gap-2">
                <LoadingSpinner />
                <span className="text-slate-500 text-sm">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t p-4">
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={
                selectedJobId
                  ? "Ask a question about the scraped content..."
                  : "Select a scraper job first"
              }
              disabled={!selectedJobId || loading}
              className="flex-1 p-2 border rounded-md text-sm resize-none"
              rows={2}
            />
            <div className="flex flex-col gap-2">
              <PrimaryBlueButton
                label="Send"
                onClick={sendMessage}
                disabled={!input.trim() || !selectedJobId || loading}
              />
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-400 mt-2">
            Press Enter to send, Shift+Enter for new line
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        <strong>Direct vector search only.</strong> Answers come straight from the embedding index — no system prompt, no persona, no handoff logic. This is the fastest way to catch ingestion problems before they affect real conversations.
      </div>
    </div>
  );
}
